/**
 * Global Revisions Service - API integration and utility functions
 * Following modular architecture pattern from existing utils
 */

import { authenticatedFetch } from './authenticatedFetch.js';

const API_BASE_URL = '/api/stores';

/**
 * Service class for global revisions API calls and date logic
 */
class GlobalRevisionsService {
    /**
     * Fetch recent global revisions for dashboard display
     * Automatically handles test vs production mode
     */
    static async getRecentRevisions(limit = 10, testMode = true) {
        try {
            const response = await authenticatedFetch(
                `${API_BASE_URL}/global-revisions/recent?limit=${limit}&test_mode=${testMode}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch recent revisions');
            }

            return {
                revisions: data.revisions || [],
                selectedWeek: data.selectedWeek,
                stats: data.stats || {},
                testMode: data.testMode,
                success: true
            };
        } catch (error) {
            console.error('Error fetching recent global revisions:', error);
            return {
                revisions: [],
                selectedWeek: null,
                stats: {},
                testMode,
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Fetch all global revisions with pagination for modal display
     */
    static async getAllRevisions({
        limit = 20,
        offset = 0,
        search = '',
        activeOnly = true,
        dateFrom = null,
        dateTo = null
    } = {}) {
        try {
            const params = new URLSearchParams({
                limit: limit.toString(),
                offset: offset.toString(),
                active_only: activeOnly.toString()
            });

            if (search.trim()) {
                params.append('search', search.trim());
            }

            if (dateFrom) {
                params.append('date_from', dateFrom);
            }

            if (dateTo) {
                params.append('date_to', dateTo);
            }

            const response = await authenticatedFetch(
                `${API_BASE_URL}/global-revisions/all?${params.toString()}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch all revisions');
            }

            return {
                revisions: data.revisions || [],
                pagination: data.pagination || {},
                stats: data.stats || {},
                filters: data.filters || {},
                success: true
            };
        } catch (error) {
            console.error('Error fetching all global revisions:', error);
            return {
                revisions: [],
                pagination: { hasMore: false },
                stats: {},
                filters: {},
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Fetch global revisions statistics
     */
    static async getStats() {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/global-revisions/stats`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch revisions stats');
            }

            return {
                stats: data.stats || {},
                recentCount: data.recentCount || 0,
                hasRecentActivity: data.hasRecentActivity || false,
                success: true
            };
        } catch (error) {
            console.error('Error fetching global revisions stats:', error);
            return {
                stats: {},
                recentCount: 0,
                hasRecentActivity: false,
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Format revision date for display
     */
    static formatRevisionDate(dateString) {
        if (!dateString) return 'No date';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    }

    /**
     * Format revision date with time for detailed view
     */
    static formatRevisionDateTime(dateString) {
        if (!dateString) return 'No date';

        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    }

    /**
     * Get revision type color for UI display
     */
    static getRevisionTypeColor(type) {
        const colorMap = {
            'removal': 'text-red-600 bg-red-50 border-red-200',
            'offer': 'text-blue-600 bg-blue-50 border-blue-200',
            'branding': 'text-purple-600 bg-purple-50 border-purple-200',
            'legal': 'text-amber-600 bg-amber-50 border-amber-200',
            'contact': 'text-green-600 bg-green-50 border-green-200',
            'coverage': 'text-indigo-600 bg-indigo-50 border-indigo-200',
            'general': 'text-slate-600 bg-slate-50 border-slate-200'
        };

        return colorMap[type] || colorMap['general'];
    }

    /**
     * Get revision type icon for UI display
     */
    static getRevisionTypeIcon(type) {
        const iconMap = {
            'removal': '🗑️',
            'offer': '💰',
            'branding': '🎨',
            'legal': '⚖️',
            'contact': '📞',
            'coverage': '🗺️',
            'general': '📄'
        };

        return iconMap[type] || iconMap['general'];
    }

    /**
     * Truncate revision text for card display
     */
    static truncateRevisionText(text, maxLength = 100) {
        if (!text) return '';

        if (text.length <= maxLength) {
            return text;
        }

        return text.substring(0, maxLength).trim() + '...';
    }

    /**
     * Check if a revision is recent (within specified days)
     */
    static isRecentRevision(dateString, days = 7) {
        if (!dateString) return false;

        try {
            const revisionDate = new Date(dateString);
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);

            return revisionDate >= cutoffDate;
        } catch (error) {
            return false;
        }
    }

    /**
     * Group revisions by client for organized display
     */
    static groupRevisionsByClient(revisions) {
        const groups = {};

        revisions.forEach(revision => {
            const client = revision.client || 'Unknown Client';
            if (!groups[client]) {
                groups[client] = [];
            }
            groups[client].push(revision);
        });

        return groups;
    }

    /**
     * Sort revisions by date (newest first)
     */
    static sortRevisionsByDate(revisions, descending = true) {
        return [...revisions].sort((a, b) => {
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);

            return descending ? dateB - dateA : dateA - dateB;
        });
    }

    /**
     * Filter revisions by search term
     */
    static filterRevisions(revisions, searchTerm) {
        if (!searchTerm.trim()) {
            return revisions;
        }

        const term = searchTerm.toLowerCase();

        return revisions.filter(revision =>
            (revision.client && revision.client.toLowerCase().includes(term)) ||
            (revision.revision && revision.revision.toLowerCase().includes(term)) ||
            (revision.type && revision.type.toLowerCase().includes(term))
        );
    }
}

export default GlobalRevisionsService;
